import React from 'react'

import { routes, useFlag } from '@sketch/modules-common'

// Utils
import {
  getWorkspaceUserRolePermissions,
  workspaceSettingsPermissions,
  useCanSubscribeViaStripe,
} from 'modules/workspace/utils'

// Local Icons
import { icons } from '../../icons'

// Hooks
import { useDiscover } from '@sketch/discover'

// Components
import { SkeletonSidebarList, SelectDropdownLinkItem } from '@sketch/components'

// Local Components
import HelpMenu from '../HelpMenu'
import DiscoverNewContentPill from '../DiscoverNewContentPill'
import SettingsMenu from '../SettingsMenu/SettingsMenu'

// Styles
import { Wrapper } from './BottomMenu.styles'

// Types
import {
  useGetCustomerProviderQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

import { useWorkspaceSettings } from 'modules/workspace/operations'

interface BottomMenuProps {
  workspace?: WorkspaceMinimalFragment
}

interface DerivedMenusProps {
  workspace: WorkspaceMinimalFragment
}

/**
 *
 * DerivedMenus
 *
 * These menus need some data to be computed and should
 * render a skeleton UI.
 *
 * They were separated from the "BottomMenu" because the "HelpMenu"
 * doesn't need a Skeleton UI and can be rendered straight away
 *
 */
const DerivedMenus: React.FC<DerivedMenusProps> = ({ workspace }) => {
  const { hasNewContent } = useDiscover()

  // Clear FF when its released
  // https://github.com/sketch-hq/Cloud/issues/16994
  const isCommunityCanvasFFEnabled = useFlag('community-canvas')

  const workspaceId = workspace.identifier
  // Some queries will be skipped if there is no customerId
  // so we default it to an empty string
  const customerId = workspace.customer?.identifier || ''

  const {
    isAdmin,
    canManageBillingSetting,
    isGuest,
  } = getWorkspaceUserRolePermissions(workspace)

  const customerProvider = useGetCustomerProviderQuery({
    variables: { customerId },
    skip: !(canManageBillingSetting && customerId),
  })

  const {
    data: { customer, billingHistoryCount },
    loading: isWorkspaceSettingsLoading,
  } = useWorkspaceSettings(workspace)

  const { source } = useCanSubscribeViaStripe(
    workspace.identifier,
    workspace.customer?.identifier
  )

  const loading = customerProvider.loading || isWorkspaceSettingsLoading

  if (loading) {
    return <SkeletonSidebarList />
  }

  const {
    showWorkspaceSettingsBilling: showBilling,
    showWorkspaceSettingsGeneral: showGeneral,
    showWorkspaceSettingsPeople: showPeople,
    showWorkspacePermissionGroup: showPermissionGroup,
    showDiscover,
    showCommunity,
  } = workspaceSettingsPermissions({
    workspace,
    customerProvider: customerProvider.data,
    billingHistoryCount: billingHistoryCount,
    customer: customer,
    source,
  })

  const canRenderSettingsMenu = !isGuest
  const hasPeopleMenuOnly =
    !showBilling && !showGeneral && canRenderSettingsMenu

  const settingsMenuPermissions = {
    isAdmin,
    showWorkspaceSettingsBilling: showBilling,
    showWorkspaceSettingsGeneral: showGeneral,
    showWorkspaceSettingsPeople: showPeople,
    showWorkspacePermissionGroup: showPermissionGroup,
  }

  return (
    <>
      {showDiscover && (
        <SelectDropdownLinkItem
          icon={icons.CompassIcon}
          text="Discover"
          to={routes.WORKSPACE_DISCOVER.create({ workspaceId })}
          additional={hasNewContent && <DiscoverNewContentPill />}
        />
      )}

      {isCommunityCanvasFFEnabled && showCommunity && (
        <SelectDropdownLinkItem
          icon={icons.CommunityIcon}
          text="Community"
          to={routes.COMMUNITY_CANVAS.create({})}
        />
      )}

      {hasPeopleMenuOnly ? (
        <SelectDropdownLinkItem
          icon={icons.EmailIcon}
          text="People"
          to={routes.WORKSPACE_SETTINGS_PEOPLE.create({ workspaceId })}
        />
      ) : (
        canRenderSettingsMenu && (
          <SettingsMenu
            workspace={workspace}
            permissions={settingsMenuPermissions}
          />
        )
      )}
    </>
  )
}

/**
 * BottomMenu
 *
 * Renders the bottom menu on the sidebar with
 * Discover, Settings and Help menu
 */
const BottomMenu: React.FC<BottomMenuProps> = ({ workspace }) => {
  return (
    <Wrapper data-testid="bottom-menu">
      {workspace && <DerivedMenus workspace={workspace} />}

      <HelpMenu
        title="Help"
        userCanAdminister={workspace?.userCanAdminister}
        workspaceId={workspace?.identifier}
        workspaceName={workspace?.name}
      />
    </Wrapper>
  )
}

export default BottomMenu
